import HyperDX from '@hyperdx/browser'

const PROD_PORTAL_HOST = 'portal.optictruckworks.com'
const STG_PORTAL_HOST = 'stg-portal.optictruckworks.com'

/**
 * Only our API origins get the W3C `traceparent` header, so otw-api Server spans
 * nest under the portal's fetch/XHR Client spans (HyperDX Service Map edge).
 * Anchored to the origin: never Firestore/googleapis (their CORS rejects it).
 */
export const PROD_API_TRACE_TARGETS: RegExp[] = [/^https:\/\/api\.optictruckworks\.com(\/|$)/i]
export const STG_API_TRACE_TARGETS: RegExp[] = [/^https:\/\/dev-api\.optictruckworks\.com(\/|$)/i]

const hyperDxOtlpBase = (
  process.env.VITE_HYPERDX_OTLP_URL || 'https://hyperdx.devrecated.com'
).replace(/\/$/, '')

/** Skip RUM fetch instrumentation for noisy or sensitive hosts (Firestore Listen/channel, auth, telemetry). */
export const buildPortalHyperDxIgnoreUrls = (otlpBaseUrl: string): (string | RegExp)[] => {
  const patterns: RegExp[] = [
    /firestore\.googleapis\.com/i,
    /firebase\.googleapis\.com/i,
    /firebaseinstallations\.googleapis\.com/i,
    /identitytoolkit\.googleapis\.com/i,
    /securetoken\.googleapis\.com/i,
    /firebasestorage\.googleapis\.com/i,
    /storage\.googleapis\.com/i,
    /api\.ipify\.org/i,
  ]
  try {
    const host = new URL(otlpBaseUrl).hostname
    if (host) {
      patterns.push(new RegExp(host.replace(/\./g, '\\.'), 'i'))
    }
  } catch {
    /* ignore invalid OTLP URL at build time */
  }
  return patterns
}

const portalHyperDxIgnoreUrls = buildPortalHyperDxIgnoreUrls(hyperDxOtlpBase)

type PortalHyperDxInitOptions = {
  apiKey: string
  service: string
  tracePropagationTargets: RegExp[]
}

const initPortalHyperDxClient = ({
  apiKey,
  service,
  tracePropagationTargets,
}: PortalHyperDxInitOptions): void => {
  HyperDX.init({
    apiKey,
    url: hyperDxOtlpBase,
    service,
    tracePropagationTargets,
    ignoreUrls: portalHyperDxIgnoreUrls,
    // API bodies/headers for api.optictruckworks.com; Firestore long-poll excluded via ignoreUrls.
    consoleCapture: false,
    advancedNetworkCapture: true,
  })
}

/** HyperDX only on deployed portal hostnames — not localhost or preview URLs. */
export function initPortalHyperDx(): boolean {
  const apiKey = process.env.VITE_HYPERDX_API_KEY
  if (!apiKey) {
    return false
  }

  const host = window.location.hostname

  if (host === PROD_PORTAL_HOST) {
    initPortalHyperDxClient({
      apiKey,
      service: 'otw-portal',
      tracePropagationTargets: PROD_API_TRACE_TARGETS,
    })
    return true
  }

  if (host === STG_PORTAL_HOST) {
    initPortalHyperDxClient({
      apiKey,
      service: 'stg-otw-portal',
      tracePropagationTargets: STG_API_TRACE_TARGETS,
    })
    return true
  }

  return false
}

export function attachPortalHyperDxClientIp(): void {
  fetch('https://api.ipify.org?format=json')
    .then(response => response.json())
    .then(data => {
      HyperDX.setGlobalAttributes({ userIp: data.ip })
    })
    .catch(error => {
      console.error('Error fetching IP:', error)
    })
}
